import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import SupportAgentComposer from './support-agent-composer';
import SupportAgentMessageList from './support-agent-message-list';
import { useSupportAgentChat } from './use-support-agent-chat';
import { useSupportAgentVisibility } from './support-agent-visibility';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Close from '@mui/icons-material/Close';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

const DRAG_THRESHOLD_PX = 5;
const VIEWPORT_MARGIN_PX = 8;

type WidgetPosition = {
  left: number;
  top: number;
};

function clampPosition(
  left: number,
  top: number,
  width: number,
  height: number
): WidgetPosition {
  const maxLeft = Math.max(
    VIEWPORT_MARGIN_PX,
    window.innerWidth - width - VIEWPORT_MARGIN_PX
  );
  const maxTop = Math.max(
    VIEWPORT_MARGIN_PX,
    window.innerHeight - height - VIEWPORT_MARGIN_PX
  );

  return {
    left: Math.min(Math.max(left, VIEWPORT_MARGIN_PX), maxLeft),
    top: Math.min(Math.max(top, VIEWPORT_MARGIN_PX), maxTop),
  };
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(
    target.closest(
      'button, a, input, textarea, select, [role="button"], [contenteditable="true"]'
    )
  );
}

export default function SupportAgent() {
  const [position, setPosition] = useState<WidgetPosition | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { visible, setVisible } = useSupportAgentVisibility();
  const { messages, isSending, send, retry } = useSupportAgentChat();

  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    originLeft: number;
    originTop: number;
    dragging: boolean;
  } | null>(null);

  useEffect(() => {
    if (!visible) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setVisible(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, setVisible]);

  useEffect(() => {
    if (!position) {
      return;
    }

    const handleResize = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      const { width, height } = container.getBoundingClientRect();
      setPosition((current) =>
        current
          ? clampPosition(current.left, current.top, width, height)
          : current
      );
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [position]);

  const handleHeaderPointerDown = (
    event: ReactPointerEvent<HTMLDivElement>
  ) => {
    if (
      event.button !== 0 ||
      !containerRef.current ||
      isInteractiveTarget(event.target)
    ) {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      originLeft: rect.left,
      originTop: rect.top,
      dragging: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleHeaderPointerMove = (
    event: ReactPointerEvent<HTMLDivElement>
  ) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !containerRef.current) {
      return;
    }

    const dx = event.clientX - drag.startClientX;
    const dy = event.clientY - drag.startClientY;

    if (!drag.dragging) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) {
        return;
      }
      drag.dragging = true;
      setIsDragging(true);
    }

    const { width, height } = containerRef.current.getBoundingClientRect();
    setPosition(
      clampPosition(drag.originLeft + dx, drag.originTop + dy, width, height)
    );
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }

    dragRef.current = null;
    setIsDragging(false);
  };

  if (!visible) {
    return null;
  }

  return (
    <Box
      ref={containerRef}
      data-testid="support-agent-widget"
      sx={{
        position: 'fixed',
        ...(position
          ? {
              left: position.left,
              top: position.top,
              right: 'auto',
              bottom: 'auto',
            }
          : {
              bottom: 24,
              right: 24,
            }),
        zIndex: (theme) => theme.zIndex.speedDial,
        userSelect: isDragging ? 'none' : 'auto',
      }}
    >
      <Paper
        elevation={0}
        role="dialog"
        aria-modal="false"
        aria-labelledby="support-agent-panel-title"
        sx={{
          width: {
            xs: 'min(100vw - 32px, 380px)',
            sm: 380,
          },
          height: {
            xs: 'min(70vh, 520px)',
            sm: 520,
          },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? theme.palette.colors.primary[700]
              : theme.palette.semantic.surface.bg1,
          border: (theme) =>
            `0.5px solid ${
              theme.palette.mode === 'dark'
                ? theme.palette.colors.neutral[600]
                : theme.palette.colors.neutral[400]
            }`,
          borderRadius: 2,
          boxShadow: '1px 0px 6px 0px #0000001F',
        }}
      >
        <Box
          data-testid="support-agent-drag-handle"
          onPointerDown={handleHeaderPointerDown}
          onPointerMove={handleHeaderPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            bgcolor: 'transparent',
            cursor: isDragging ? 'grabbing' : 'grab',
            touchAction: 'none',
            borderBottom: (theme) =>
              `1px solid ${
                theme.palette.mode === 'dark'
                  ? theme.palette.colors.darkBlue[5]
                  : theme.palette.semantic.outline.light
              }`,
            '& button': {
              cursor: 'pointer',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
            }}
          >
            <SupportAgentIcon
              aria-hidden
              data-testid="support-agent-header-icon"
              sx={{
                fontSize: 24,
                pointerEvents: 'none',
                color: (theme) => theme.palette.semantic.text.title,
              }}
            />
            <Typography
              id="support-agent-panel-title"
              variant="h6"
              sx={{
                color: (theme) => theme.palette.semantic.text.title,
                fontWeight: 600,
              }}
            >
              Ro, an AI Support Agent
            </Typography>
          </Box>
          <IconButton
            aria-label="Close support agent"
            onClick={() => setVisible(false)}
            size="small"
            sx={{
              color: (theme) => theme.palette.semantic.icon.default,
            }}
          >
            <Close />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'transparent',
            p: 1.5,
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: '4px',
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? theme.palette.colors.darkBlue[9]
                  : theme.palette.colors.neutral[20],
            }}
          >
            <SupportAgentMessageList
              messages={messages}
              isSending={isSending}
              isActive
              onRetry={retry}
            />
          </Box>
          <SupportAgentComposer isSending={isSending} onSend={send} />
        </Box>
      </Paper>
    </Box>
  );
}
